import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField, CircularProgress, Tooltip, IconButton, Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import channelService, { type ChannelMember } from '../../../services/channelService';

// PR #684 review (M3) — deliberately permissive (not RFC 5322): this only needs to catch obvious
// typos ("john@acme", "john.acme.com") before they're saved as the approver, not reject every
// technically-unusual-but-valid address.
const EMAIL_FORMAT_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Same small info-icon-with-tooltip pattern already used in WorkflowInspectorPanel.tsx,
// DynamicMultiselectField.tsx, WorkflowToolbar.tsx and WorkflowCanvasTab.tsx — kept as a local
// copy here too rather than a shared export, matching how those files each define their own copy.
const FieldHelp: React.FC<{ text: string }> = ({ text }) => (
  <Tooltip
    title={<Box component="span" sx={{ whiteSpace: 'pre-line', display: 'block', fontSize: '0.75rem' }}>{text}</Box>}
    placement="top"
    arrow
    slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
  >
    <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }} tabIndex={-1}>
      <InfoOutlinedIcon sx={{ fontSize: 15 }} />
    </IconButton>
  </Tooltip>
);

export interface MemberSelectFieldProps {
  label: string;
  required?: boolean;
  channelId?: string;
  /** A single email — same plain string this field stored before switching to member_select
   * (e.g. approver_email/assignee_email), so the value sent to the backend is unchanged either
   * way; only how it's edited changes. */
  value: string;
  onChange: (email: string) => void;
}

/**
 * FE-07_approver_per_run_or_fixed.md — "Fixed approver (nice-to-have)" — a new, generic
 * `member_select` catalog field type for picking ONE channel member (or typing a custom email),
 * for a field that should hold a single, fixed assignee rather than a per-run `{token}`.
 *
 * Deliberately a NEW component rather than reusing WC/RecipientPicker.tsx directly: that
 * component is hard-coded to a MULTI-select `RuleFlowRecipient[]` array for the Business Rules
 * canvas's approval-step recipients list, actively used there today. Changing it to also support
 * a single scalar value risked regressing that existing flow; this instead mirrors its exact
 * approach — same `channelService.getChannelMembersAll` data source, same "pick a member or type
 * an email" UX — adapted for the single-string value this inspector field already stores.
 */
const MemberSelectField: React.FC<MemberSelectFieldProps> = ({ label, required, channelId, value, onChange }) => {
  const [members, setMembers] = useState<ChannelMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // PR #684 review (M3) — was committing every keystroke straight to the node's saved inputs via
  // onInputChange, so a typo mid-edit was saved as the approver just as much as a finished
  // address. This buffers what the admin is typing locally and only calls the parent's onChange
  // (the actual commit) on blur or when an option is picked/cleared — never per keystroke.
  const [draft, setDraft] = useState(value);
  // PR #685 review (M3) — on an invalid blur, the draft used to just sit there showing the bad
  // text forever with no indication the save was skipped; the admin could save the workflow with
  // the old approver still in effect and never notice. Reverting to the last committed value
  // (with this notice) makes the field always reflect what's actually saved.
  const [revertedNotice, setRevertedNotice] = useState(false);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    let cancelled = false;
    if (!channelId) return;
    setLoading(true);
    setError(null);
    channelService
      .getChannelMembersAll(channelId)
      .then((result) => {
        if (!cancelled) setMembers(result);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load channel members. You can still type an email directly.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [channelId]);

  const options = members.map((m) => m.email).filter(Boolean);

  const trimmedDraft = draft.trim();
  const hasFormatError = trimmedDraft !== '' && !EMAIL_FORMAT_RE.test(trimmedDraft);
  // PR #685 review (L3) — email addresses are case-insensitive; "pat@Acme.com" is the same member
  // as "pat@acme.com" and must not be flagged as an unknown/external address.
  const matchedMember = members.find((m) => m.email.toLowerCase() === trimmedDraft.toLowerCase());
  const isKnownMember = trimmedDraft !== '' && Boolean(matchedMember);
  // A policy decision (per the review), not an error: an external approver is allowed, just
  // worth flagging since it's easy to mistype a member's address into something that resolves
  // to nobody in this channel.
  //
  // PR #685 review (L3) — only warn once the member list has actually loaded successfully; while
  // loading, or after a failed fetch, "not a member" can't be trusted and was flagging every
  // address, known or not.
  const isUnknownMember = trimmedDraft !== '' && !hasFormatError && !isKnownMember && !loading && !error;

  // Returns whether `next` was committed (true) or rejected as malformed (false).
  const commit = (next: string): boolean => {
    const trimmed = next.trim();
    // Never commit an obviously malformed, non-empty address — clearing is always allowed.
    if (trimmed !== '' && !EMAIL_FORMAT_RE.test(trimmed)) return false;
    // PR #685 review (L2) — skip re-committing a value that hasn't actually changed, so blurring
    // in and out of an already-saved, untouched field doesn't re-set the node input every time.
    if (trimmed !== value) onChange(trimmed);
    return true;
  };

  return (
    <Autocomplete
      freeSolo
      size="small"
      options={options}
      // PR #685 review (M1) — passing the live draft as `value` for ANY non-empty text made MUI's
      // own inputValueIsSelectedValue check true for every typed address: getOptionLabel falls
      // back to returning the raw string unchanged for anything that isn't a listed member, which
      // then trivially equals inputValue (also the raw draft). MUI reads that as "input already
      // matches the selected value, nothing new to commit" and silently skips the freeSolo
      // Enter-commit path entirely — not just for invalid text, for ANY typed address. Only
      // reflecting a real matched member here (null otherwise) lets Enter's commit path run at all.
      value={matchedMember?.email ?? null}
      inputValue={draft}
      disabled={!channelId}
      loading={loading}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === 'input') {
          setDraft(newInputValue);
          setRevertedNotice(false);
        }
        // 'clear' (the Autocomplete's own clear button) commits immediately — there's no format
        // to validate when emptying the field.
        if (reason === 'clear') {
          setDraft('');
          setRevertedNotice(false);
          onChange('');
        }
      }}
      onBlur={() => {
        if (commit(draft)) {
          setRevertedNotice(false);
        } else {
          // PR #685 review (M3) — a malformed address is never silently left in place: revert to
          // what's actually saved rather than leaving broken text sitting in the field forever.
          setDraft(value);
          setRevertedNotice(true);
        }
      }}
      onChange={(_event, newValue) => {
        // PR #685 review (M1) — this fires for BOTH picking a listed option (reason
        // 'selectOption') and pressing Enter on freeSolo-typed text (reason 'createOption'); it
        // previously called onChange directly, so Enter bypassed commit()'s validation entirely.
        // Routing both through commit() means a malformed address typed and confirmed with Enter
        // is rejected exactly like one left via blur, and a real listed option (always a valid
        // address) still commits immediately, same as before.
        const next = newValue ?? '';
        setDraft(next);
        if (commit(next)) {
          setRevertedNotice(false);
        } else {
          setDraft(value);
          setRevertedNotice(true);
        }
      }}
      getOptionLabel={(option) => {
        const member = members.find((m) => m.email === option);
        return member ? `${member.name} (${member.email})` : String(option);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={required ? `${label} *` : label}
          placeholder={channelId ? 'Pick a channel member or type an email' : 'No channel context available'}
          error={Boolean(error) || hasFormatError}
          helperText={
            error ||
            (hasFormatError
              ? "Doesn't look like a valid email address"
              : revertedNotice
                ? "Reverted to the last saved value — that address wasn't valid"
                : isUnknownMember
                  ? 'Not a member of this channel — will be saved as an external approver'
                  : undefined)
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                <FieldHelp text="Pick a channel member or type an email directly. This is a single, fixed assignee — not a per-run value that changes based on who's involved in each run." />
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default MemberSelectField;
